import React from 'react';
import { IndianRupee, Lock, Timer, Unlock, User } from 'lucide-react';
import { Surface, Text } from '@/components/flup';

/**
 * The day at a glance, above the board.
 *
 * Four numbers answer the questions a vendor opens this screen with — how much
 * is still sellable, how much sold, what did I block, what's holding — without
 * them counting tiles. The fourth slot shows held-in-checkout only while there
 * *is* something held, and falls back to booked value otherwise, so the row
 * never carries a permanent zero.
 *
 * They read as one strip rather than four stat cards on purpose: these are a
 * glance on the way to the board, and as cards they pushed the first slot of
 * the day off the bottom of the screen.
 */

interface DaySummaryProps {
  available: number;
  booked: number;
  blocked: number;
  hold: number;
  earned: number;
}

const Metric: React.FC<{ icon: React.ReactNode; label: string; value: React.ReactNode }> = ({
  icon,
  label,
  value,
}) => (
  <div className="flex items-center gap-2 min-w-0">
    <span className="grid place-items-center w-7 h-7 rounded-flup-xs bg-flup-surface-alt text-flup-ink-3 shrink-0 [&_svg]:w-3.5 [&_svg]:h-3.5">
      {icon}
    </span>
    <Text variant="micro" tone="tertiary" as="span" lines={1}>
      {label}
    </Text>
    <Text variant="statSm" as="span" className="text-flup-ink">
      {value}
    </Text>
  </div>
);

const DaySummary: React.FC<DaySummaryProps> = ({ available, booked, blocked, hold, earned }) => (
  <Surface padding="px-4 py-2.5 sm:px-5">
    <div className="flex flex-wrap items-center gap-x-6 gap-y-3 sm:gap-x-8">
      <Metric icon={<Unlock />} label="Open to sell" value={available} />
      <Metric icon={<User />} label="Booked" value={booked} />
      <Metric icon={<Lock />} label="Blocked by you" value={blocked} />
      {hold > 0 ? (
        <Metric icon={<Timer />} label="Held in checkout" value={hold} />
      ) : (
        <Metric
          icon={<IndianRupee />}
          label="Booked value"
          value={`₹${Math.round(earned).toLocaleString('en-IN')}`}
        />
      )}
    </div>
  </Surface>
);

export default React.memo(DaySummary);
